import { useEffect, useState } from "react";
import { ArrowUpRight } from "lucide-react";
import { cn } from "@/lib/utils";
import { FrameHeader, FrameTitle, FrameDescription } from "@/components/ui/frame";
import AmyBadge from "@/components/ui/amy-badge";
import { Composer } from "./Composer";
import { EXAMPLE_PROMPTS } from "./constants";
import { ScopeWrapper } from "@/components/ui/scope-wrapper";

interface ChatEmptyStateProps {
  isStreaming: boolean;
  onSend: (text: string) => void;
}

const TITLE_WORDS = ["Ask", "Amy", "about", "your", "campaigns"];

/**
 * Whether the launcher's entrance has already played this session. The page only
 * mounts this component once conversations are fetched, so the first mount is the
 * "first fetch" — we animate then and never again (remounts render the final state).
 */
let hasPlayedIntro = false;

/** Title that reveals word-by-word on mount, with the Amy badge inline. */
function AnimatedTitle({ animate }: { animate: boolean }) {
  return (
    <>
      {TITLE_WORDS.map((word, i) => (
        <span
          key={i}
          className={cn(
            "mr-[0.28em] inline-block align-middle last:mr-0",
            animate && "duration-700 ease-out animate-in fade-in slide-in-from-bottom-3"
          )}
          style={animate ? { animationDelay: `${i * 90}ms`, animationFillMode: "both" } : undefined}
        >
          {word === "Amy" ? (
            <AmyBadge
              imgClassName="size-7 sm:size-12"
              className="inline-flex translate-y-[-0.06em] align-middle gap-1.5 p-1 pr-2.5 text-2xl font-semibold leading-snug tracking-tight sm:text-3xl"
            />
          ) : (
            word
          )}
        </span>
      ))}
    </>
  );
}

/**
 * The pre-conversation launcher: a left-aligned animated greeting, the composer
 * centered in the middle, and the starter prompts as ghost rows beneath it.
 */
export function ChatEmptyState({ isStreaming, onSend }: ChatEmptyStateProps) {
  // Freeze the decision at mount so an in-flight animation isn't cut short when
  // the guard flips; only the very first launcher of the session animates.
  const [animate] = useState(() => !hasPlayedIntro);
  useEffect(() => {
    hasPlayedIntro = true;
  }, []);

  return (
    <div className="relative flex flex-1 flex-col items-center justify-center overflow-y-auto px-4 py-10">
      <div className="w-full max-w-3xl">
        {/* greeting */}
        <FrameHeader className="px-3.5 pt-2.5 pb-3">
          <FrameTitle className="text-2xl font-semibold leading-snug tracking-tight sm:text-3xl">
            <AnimatedTitle animate={animate} />
          </FrameTitle>

          <FrameDescription
            className={cn(
              "mt-2 text-sm",
              animate && "duration-700 ease-out animate-in fade-in slide-in-from-bottom-2"
            )}
          >
            She reads your creators, statuses and conversations to answer.
          </FrameDescription>
        </FrameHeader>

        {/* composer, almost as-is, in the middle */}
        <div
          className={cn(animate && "duration-700 ease-out animate-in fade-in slide-in-from-bottom-3")}
          style={animate ? { animationDelay: "560ms", animationFillMode: "both" } : undefined}
        >
          <Composer busy={isStreaming} onSend={onSend} />
        </div>

        {/* starter prompts as ghost rows */}
        <div className="mt-1 flex flex-col px-2">
          {EXAMPLE_PROMPTS.map(({ icon: Icon, text }, i) => (
            <ScopeWrapper>
              <button
                key={text}
                onClick={() => onSend(text)}
                disabled={isStreaming}
                style={
                  animate ? { animationDelay: `${680 + i * 80}ms`, animationFillMode: "both" } : undefined
                }
                className={cn(
                  "group w-full flex items-center gap-3 rounded-lg px-3 py-2.5 text-left text-sm text-foreground/80 transition duration-200 hover:duration-0 hover:bg-sidebar hover:text-foreground disabled:opacity-50 cursor-pointer",
                  animate && "animate-in fade-in slide-in-from-bottom-2"
                )}
              >
                <Icon className="size-4 shrink-0 text-muted-foreground" />
                <span className="flex-1">{text}</span>
                <ArrowUpRight className="size-4 -translate-x-1 text-muted-foreground opacity-0 transition group-hover:translate-x-0 group-hover:opacity-100" />
              </button>
            </ScopeWrapper>
          ))}
        </div>
      </div>
    </div>
  );
}
